import { cn } from '@/lib/cn'
import { X } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'

import {
  ToastContext,
  toastToneStyles,
  type Toast,
  type ToastApi,
  type ToastOptions,
  type ToastTone,
} from './toastContext'

let nextId = 1

/** An undo prompt has to be long enough to notice and long enough to click. */
const ACTION_LIFETIME_MS = 6000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  // A ref, not state: the dismissal timer is scheduled once and has to read
  // whether the toast is being held at the moment it fires, not at the moment
  // it was scheduled.
  const held = useRef(new Set<number>())

  const dismiss = useCallback((id: number) => {
    held.current.delete(id)
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    (tone: ToastTone, message: string, options?: ToastOptions) => {
      const id = nextId++
      setToasts((current) => [
        ...current,
        { id, tone, message, action: options?.action },
      ])
      const lifetime = options?.action
        ? ACTION_LIFETIME_MS
        : tone === 'error'
          ? 8000
          : 4000
      window.setTimeout(() => {
        // Holding it open is opt-in per toast and only while the pointer is on
        // it, so a misclick is never lost to a slow reaction. Non-action toasts
        // never opt in, so this is not a general snooze.
        window.setTimeout(() => dismiss(id), held.current.has(id) ? lifetime : 0)
      }, lifetime)
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, options) => push('success', message, options),
      error: (message) => push('error', message),
      info: (message, options) => push('info', message, options),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-3 sm:items-end sm:p-4 md:bottom-auto md:top-4 md:right-4 md:items-end"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            onMouseEnter={() => held.current.add(toast.id)}
            onMouseLeave={() => held.current.delete(toast.id)}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-[var(--radius-card)] border px-3.5 py-2.5 shadow-raised',
              toastToneStyles[toast.tone].wrap,
            )}
          >
            <span className="mt-px shrink-0">{toastToneStyles[toast.tone].icon}</span>
            <p className="min-w-0 flex-1 text-pretty text-xs leading-relaxed">
              {toast.message}
            </p>
            {toast.action ? (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick()
                  dismiss(toast.id)
                }}
                className="-my-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold underline underline-offset-2 hover:bg-surface/60"
              >
                {toast.action.label}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
              className="-m-1 shrink-0 rounded p-1 opacity-60 hover:opacity-100"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
